import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import ProfileTab from './components/account/ProfileTab';
import NotificationsTab from './components/account/NotificationsTab';
import SecurityTab from './components/account/SecurityTab';

import Home from './pages/Home';

const Products = React.lazy(() => import('./pages/Products'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const Orders = React.lazy(() => import('./pages/Orders'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const OrderConfirmation = React.lazy(() => import('./pages/OrderConfirmation'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Wishlist = React.lazy(() => import('./pages/Wishlist'));
const AddressBook = React.lazy(() => import('./pages/AddressBook'));
const OrderTracking = React.lazy(() => import('./pages/OrderTracking'));
const ReturnRequest = React.lazy(() => import('./pages/ReturnRequest'));
const AccountDashboard = React.lazy(() => import('./pages/AccountDashboard'));
const AdminApp = React.lazy(() => import('./admin/AdminApp'));

// Static & Trust Pages
const Terms = React.lazy(() => import('./pages/Terms'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const ReturnPolicy = React.lazy(() => import('./pages/ReturnPolicy'));
const ShippingPolicy = React.lazy(() => import('./pages/ShippingPolicy'));

function App() {
  return (
    <ErrorBoundary>
      <React.Suspense fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-offwhite">
          <div className="w-12 h-12 border-4 border-gold/20 border-t-gold rounded-full animate-spin mb-4"></div>
          <h2 className="font-playfair text-gold text-xs tracking-[0.3em] uppercase animate-pulse">Poshe Styles</h2>
        </div>
      }>
        <Routes>
          {/* Admin Panel — separate layout, no Navbar/Footer */}
          <Route path="/poshe-admin/*" element={<AdminApp />} />

          {/* Main Website */}
          <Route path="*" element={
            <div className="flex flex-col min-h-screen bg-offwhite font-inter">
              <Navbar />
              <main className="flex-1">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/checkout" element={<Checkout />} />
                  
                  {/* Legacy top-level routes redirecting to account if logged in, or serving as guest view */}
                  <Route path="/orders" element={<Navigate to="/account/orders" replace />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                
                {/* Account Dashboard with Nested Routes */}
                <Route path="/account" element={<PrivateRoute><AccountDashboard /></PrivateRoute>}>
                  <Route path="profile" element={<ProfileTab />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="addresses" element={<AddressBook />} />
                  <Route path="wishlist" element={<Wishlist />} />
                  <Route path="notifications" element={<NotificationsTab />} />
                  <Route path="security" element={<SecurityTab />} />
                </Route>

                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/returns-policy" element={<ReturnPolicy />} />
                <Route path="/shipping" element={<ShippingPolicy />} />
                <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />

                <Route path="/orders/:id" element={<OrderTracking />} />
                <Route path="/orders/:id/return" element={<PrivateRoute><ReturnRequest /></PrivateRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
        } />
      </Routes>
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default App;

